// MBA Lab — Team Retrospective Facilitator UI. Imports only lib/core.js,
// lib/retro-prompts.js, lib/retro-model.js, and lib/tool-dock.js.
//
// Three phases, no fourth: Prep (what this retro is about, plus last retro's
// commitments marked done / not-done), Run (a display-only 5/15/10 timebox
// card and the three capture fields), Commit (exactly two commitments with an
// owner each, plus the moment you'll review them). There is deliberately no
// format picker and no timer - see lib/retro-prompts.js for why.
//
// Persistence is tiered, following accountability-planner.js's precedent but
// splitting the state differently. sessionStorage holds EVERYTHING, gone once
// the tab closes. A separate opt-in "remember" toggle additionally writes to
// localStorage - but only the four fields in the model's KEEP-allowlist
// (stripSessionNotes): the deliverable, the two commitments, and the review
// moment. The Run-phase capture fields are never written to disk under any
// circumstance. They hold candid observations about named teammates, typed in
// a room where everyone can see the screen; the commitments are the part
// that's supposed to outlive the meeting, and reading them back at the start
// of the next retro is the whole compounding loop.
import { store, sessionStore, download, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import { CAPTURE_PROMPTS } from './lib/retro-prompts.js';
import {
  COMMITMENT_SLOTS,
  SCHEMA_VERSION,
  createInitialState,
  setDeliverable,
  setNote,
  setCommitmentText,
  setCommitmentOwner,
  setReviewMoment,
  setPriorCommitmentDone,
  commitmentCount,
  isComplete,
  sectionsStarted,
  hasUserEdits,
  seedFromRemembered,
  parseSavedState,
  persistState,
  buildMarkdown,
} from './lib/retro-model.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'team-retrospective-facilitator';
const AUTOSAVE_DEBOUNCE_MS = 400;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

class TeamRetrospectiveFacilitator {
  constructor() {
    this.form = byId('retro-form');
    this.cacheElements();

    this.sessionStore = sessionStore(TOOL_NAME);
    // ONLY the persistent tier is wrapped, and that is the whole design: it is the tier that
    // already passes through stripSessionNotes, so the Run-phase notes about named teammates
    // cannot reach the account by any path. The session tier - which holds everything - is
    // untouched and never leaves this tab. The server re-checks the allowlist regardless.
    this.persistentStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.rememberPrefStore = store(`${TOOL_NAME}-remember`);
    this.remember = this.rememberPrefStore.load() === true;
    if (this.els.rememberToggle) this.els.rememberToggle.checked = this.remember;

    this.rafId = null;
    this.saveTimer = null;
    this.hasEngaged = false;
    this.hasFiredResult = false;
    this.startOverConfirming = false;
    this.startOverTimer = null;

    this.dock = initToolDock({
      dock: byId('retro-dock'),
      jumpTarget: byId('retro-summary'),
      returnTarget: byId('retro-form'),
    });

    this.state = this.loadInitialState();
    this.renderAll();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.els = {
      deliverable: byId('retro-deliverable'),

      prior: byId('retro-prior'),
      priorList: byId('retro-prior-list'),
      priorContext: byId('retro-prior-context'),

      reviewMoment: byId('retro-review-moment'),

      progressText: byId('retro-progress-text'),
      startOver: byId('retro-start-over'),
      rememberToggle: byId('retro-remember-toggle'),

      print: byId('retro-print'),
      downloadBtn: byId('retro-download'),
      copy: byId('retro-copy'),
      actionFeedback: byId('retro-action-feedback'),

      summary: byId('retro-summary'),
    };
  }

  /**
   * Session state wins outright - it's this tab's in-progress retro. Only when
   * there isn't one does the persistent tier matter, and then it is never
   * restored as "your retro": it is seeded in read-only as LAST retro's
   * commitments (seedFromRemembered), which is the two-week loop the article
   * describes. Seeding deliberately leaves userEdited false, so a returning
   * visitor who reads the loop-closer and closes the tab has not "engaged".
   */
  loadInitialState() {
    const savedSession = this.sessionStore.load();
    if (savedSession) {
      const result = parseSavedState(savedSession);
      if (result.status === 'ok' || result.status === 'migrated') return result.state;
    }

    const remembered = this.persistentStore.load();
    if (remembered) {
      const result = parseSavedState(remembered);
      if (result.status === 'ok' || result.status === 'migrated') return seedFromRemembered(result.state);
    }

    return createInitialState();
  }

  wireEvents() {
    this.form.addEventListener('input', (e) => this.handleFieldChange(e));
    this.form.addEventListener('change', (e) => this.handleFieldChange(e));

    if (this.els.startOver) this.els.startOver.addEventListener('click', () => this.handleStartOver());
    if (this.els.rememberToggle) {
      this.els.rememberToggle.addEventListener('change', () => this.handleRememberToggle(this.els.rememberToggle.checked));
    }
    if (this.els.print) this.els.print.addEventListener('click', () => this.handlePrint());
    if (this.els.downloadBtn) this.els.downloadBtn.addEventListener('click', () => this.handleDownload());
    if (this.els.copy) this.els.copy.addEventListener('click', () => this.handleCopy());
  }

  handleFieldChange(e) {
    const el = e.target;
    const before = this.state;

    if (el === this.els.deliverable) {
      this.state = setDeliverable(this.state, el.value);
    } else if (el.matches('[data-retro-note]')) {
      this.state = setNote(this.state, el.dataset.retroNote, el.value);
    } else if (el.matches('[data-retro-commitment-text]')) {
      this.state = setCommitmentText(this.state, Number(el.dataset.retroCommitmentText), el.value);
    } else if (el.matches('[data-retro-commitment-owner]')) {
      this.state = setCommitmentOwner(this.state, Number(el.dataset.retroCommitmentOwner), el.value);
    } else if (el === this.els.reviewMoment) {
      this.state = setReviewMoment(this.state, el.value);
    } else if (el.matches('[data-retro-prior-done]')) {
      this.state = setPriorCommitmentDone(this.state, Number(el.dataset.retroPriorDone), el.value === 'yes');
    } else {
      return;
    }

    if (this.state === before) return;
    // Asked of the model, not assumed from "a handler ran" - the model is the
    // one thing that knows the difference between a real edit and a no-op
    // (an out-of-range slot write) or a seeded value.
    if (hasUserEdits(this.state)) this.markEngaged();
    this.scheduleUpdate();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  // --- Rendering ---------------------------------------------

  renderAll() {
    this.applyStateToForm();
    this.renderPriorCommitments();
    this.renderSummary();
    this.renderProgress();
  }

  applyStateToForm() {
    if (this.els.deliverable) this.els.deliverable.value = this.state.deliverable;
    this.form.querySelectorAll('[data-retro-note]').forEach((el) => {
      el.value = this.state[el.dataset.retroNote] || '';
    });
    this.form.querySelectorAll('[data-retro-commitment-text]').forEach((el) => {
      el.value = this.state.commitments[Number(el.dataset.retroCommitmentText)]?.text || '';
    });
    this.form.querySelectorAll('[data-retro-commitment-owner]').forEach((el) => {
      el.value = this.state.commitments[Number(el.dataset.retroCommitmentOwner)]?.owner || '';
    });
    if (this.els.reviewMoment) this.els.reviewMoment.value = this.state.reviewMoment;
  }

  /**
   * The loop-closer. Prior commitments render as read-only text with a single
   * did-we / didn't-we control each - there is no way to edit last retro's
   * wording, because quietly rewriting a commitment you didn't keep is the
   * exact failure the review moment exists to catch.
   */
  renderPriorCommitments() {
    if (!this.els.prior || !this.els.priorList) return;
    const prior = this.state.priorCommitments;
    this.els.prior.hidden = prior.length === 0;
    if (prior.length === 0) return;

    if (this.els.priorContext) {
      const bits = [];
      if (this.state.priorDeliverable) bits.push(`After: ${this.state.priorDeliverable}`);
      if (this.state.priorReviewMoment) bits.push(`Review moment you set: ${this.state.priorReviewMoment}`);
      this.els.priorContext.textContent = bits.join(' · ');
      this.els.priorContext.hidden = bits.length === 0;
    }

    clearChildren(this.els.priorList);
    prior.forEach((item, index) => {
      const li = document.createElement('li');
      li.className = 'tool-retro-prior-item';

      const text = document.createElement('p');
      text.className = 'tool-retro-prior-text';
      text.textContent = item.owner ? `${item.text} (${item.owner})` : item.text;
      li.appendChild(text);

      const group = document.createElement('div');
      group.className = 'tool-form-chip-group';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', `Did we do this? ${item.text}`);

      for (const option of [{ value: 'yes', label: 'Did it' }, { value: 'no', label: 'Didn\'t' }]) {
        const chip = document.createElement('label');
        chip.className = 'tool-form-chip tool-retro-prior-chip';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `retro-prior-done-${index}`;
        input.value = option.value;
        input.dataset.retroPriorDone = String(index);
        input.checked = item.done === (option.value === 'yes');
        chip.appendChild(input);
        chip.appendChild(document.createTextNode(` ${option.label}`));
        group.appendChild(chip);
      }

      li.appendChild(group);
      this.els.priorList.appendChild(li);
    });
  }

  renderProgress() {
    const count = commitmentCount(this.state);
    const text = `${count} of ${COMMITMENT_SLOTS} commitments`;
    if (this.els.progressText) this.els.progressText.textContent = text;
    this.dock?.setReading(text);
    this.dock?.setComplete(isComplete(this.state));
  }

  /** Text-only, built node by node - never innerHTML, so a pasted theme can't
   * become markup. Mirrors buildMarkdown()'s section order exactly so the
   * on-screen summary and the downloaded file never disagree. */
  renderSummary() {
    const el = this.els.summary;
    if (!el) return;
    clearChildren(el);

    const header = document.createElement('header');
    header.className = 'tool-retro-summary-header';
    const h2 = document.createElement('h2');
    h2.textContent = 'Team retrospective';
    header.appendChild(h2);
    const sub = document.createElement('p');
    sub.className = 'tool-retro-summary-subtitle';
    sub.textContent = this.state.deliverable || '[Which deliverable is this retro about?]';
    header.appendChild(sub);
    el.appendChild(header);

    if (this.state.priorCommitments.length > 0) {
      const items = this.state.priorCommitments.map((prior) => {
        const verdict = prior.done === null ? 'not reviewed' : prior.done ? 'done' : 'not done';
        const owner = prior.owner ? ` (${prior.owner})` : '';
        return `${prior.text}${owner} - ${verdict}`;
      });
      el.appendChild(this.buildSection('Last time we committed to', items));
    }

    for (const prompt of CAPTURE_PROMPTS) {
      const themes = String(this.state[prompt.key] || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      el.appendChild(this.buildSection(prompt.label, themes.length ? themes : ['[Nothing captured]']));
    }

    const commitments = this.state.commitments.map((slot, i) => {
      if (!slot.text.trim()) return `${i + 1}. [Not decided yet]`;
      return `${i + 1}. ${slot.text} - owner: ${slot.owner || '[unassigned]'}`;
    });
    el.appendChild(this.buildSection('Our two commitments', commitments));

    el.appendChild(this.buildSection('Review moment', [
      this.state.reviewMoment || '[When will you check these two? Name it now.]',
    ]));

    const footer = document.createElement('footer');
    footer.className = 'tool-retro-summary-footer';
    footer.textContent = 'Built with the free MBA Lab team retrospective facilitator — mbalab.net/tools/team-retrospective-facilitator';
    el.appendChild(footer);
  }

  buildSection(title, items) {
    const section = document.createElement('section');
    section.className = 'tool-retro-summary-section';
    const h3 = document.createElement('h3');
    h3.textContent = title;
    section.appendChild(h3);
    const ul = document.createElement('ul');
    for (const item of items) {
      const li = document.createElement('li');
      li.textContent = item;
      ul.appendChild(li);
    }
    section.appendChild(ul);
    return section;
  }

  // --- Update / persistence ---------------------------------------------

  scheduleUpdate() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.renderSummary();
      this.renderProgress();
      this.scheduleAutosave();
      this.maybeTrackResult();
    });
  }

  /** Fires once, the first time both commitment slots have real text. Coarse
   * params only - a count and a phase count, never the commitments themselves. */
  maybeTrackResult() {
    if (this.hasFiredResult || !isComplete(this.state)) return;
    this.hasFiredResult = true;
    // result_v2, not result: tool_result is at GA4's param-count ceiling - see analytics-events.js.
    trackToolAction(TOOL_NAME, 'result_v2', {
      commitment_count: commitmentCount(this.state),
      sections_started: sectionsStarted(this.state),
    });
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    this.persist();
  }

  /** The one write path, model-owned and unit-tested (persistState). Session
   * always; localStorage only while "remember" is on, and actively cleared
   * the moment it isn't. */
  persist() {
    return persistState({
      state: this.state,
      remember: this.remember,
      session: this.sessionStore,
      persistent: this.persistentStore,
    });
  }

  /** Writes or clears the persistent tier immediately on toggle, never at the
   * next debounced autosave - turning "remember" off has to mean the
   * localStorage key is gone now, not gone if the tab happens to stay open
   * another 400ms. */
  async handleRememberToggle(checked) {
    this.remember = checked === true;
    this.rememberPrefStore.save(this.remember);
    if (!this.remember) {
      // "Remember" is the consent for BOTH tiers of durability - this device and the account.
      // Turning it off has to remove the account's copy too, or the thing the user just asked to
      // forget survives on a server and reappears on their next device.
      this.persistentStore.deleteAccountState();
      return;
    }

    // HYDRATE BEFORE THE FIRST PERSIST. The page-load hydrate is gated on this same toggle, so a
    // fresh device turning it on has never fetched - and on that device the toggle plausibly MEANS
    // "get my stuff". Persisting first would stamp the current session state with a now() clock,
    // strictly newer than whatever the account holds, and the server would dutifully replace last
    // retro's commitments - the whole compounding loop - with this device's defaults. So: fetch
    // first, then write over the account only when this session actually holds a person's work.
    await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION).catch(() => {});
    if (this.state.userEdited) {
      this.persist();
      return;
    }

    // Untouched session + a fetched account tier: surface it the same way a page load would -
    // last retro's commitments as read-only context - rather than leaving the fetch invisible
    // until the next visit.
    const remembered = this.persistentStore.load();
    if (remembered) {
      const parsed = parseSavedState(remembered);
      if (parsed.status === 'ok' || parsed.status === 'migrated') {
        this.state = seedFromRemembered(parsed.state);
        this.renderAll();
      }
    }
  }

  // --- Actions ---------------------------------------------

  handleStartOver() {
    if (!this.startOverConfirming) {
      this.startOverConfirming = true;
      this.els.startOver.textContent = 'Click again to confirm';
      this.startOverTimer = setTimeout(() => {
        this.startOverConfirming = false;
        this.els.startOver.textContent = 'Start over';
      }, 4000);
      return;
    }

    clearTimeout(this.startOverTimer);
    this.startOverConfirming = false;
    this.els.startOver.textContent = 'Start over';
    this.sessionStore.clear();
    this.persistentStore.deleteAccountState();
    this.state = createInitialState();
    this.hasFiredResult = false;
    this.renderAll();
  }

  handlePrint() {
    trackToolAction(TOOL_NAME, 'print');
    this.showActionFeedback('Opening print…');
    window.print();
  }

  handleDownload() {
    download('team-retrospective.md', 'text/markdown', buildMarkdown(this.state));
    this.showActionFeedback('Downloaded.');
    trackToolAction(TOOL_NAME, 'download');
  }

  async handleCopy() {
    const markdown = buildMarkdown(this.state);
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(markdown);

    this.showActionFeedback(copied ? 'Copied!' : 'Could not copy - try downloading instead.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  showActionFeedback(message) {
    if (!this.els.actionFeedback) return;
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('retro-form')) return;
  // Gated on the SAME opt-in that governs this device's own persistent tier: with "remember" off,
  // there is no request at all, in either direction. Turning it on is what starts syncing, which
  // keeps one switch meaning one thing - "keep this past today" - rather than two.
  if (store(`${TOOL_NAME}-remember`).load() === true) {
    await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  }
  new TeamRetrospectiveFacilitator();
});
